import React, { useRef, useEffect, useMemo, forwardRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
}

const RichTextEditor = forwardRef<ReactQuill, RichTextEditorProps>(({
  value,
  onChange,
  placeholder = 'Start typing...',
  className = '',
  readOnly = false,
}, ref) => {
  const quillRef = useRef<ReactQuill>(null);

  // Memoize modules and formats to prevent unnecessary re-renders
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  }), []);

  const formats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link'
  ], []);

  const isFullHeight = className.includes('h-full');
  const isFixedHeight = className.includes('h-96');

  // Handle the findDOMNode warning by using a ref and proper initialization
  useEffect(() => {
    if (quillRef.current) {
      // Access the Quill instance through the ref
      const quill = quillRef.current.getEditor();
      if (quill) {
        // Ensure proper initialization and prevent findDOMNode warnings
        quill.on('text-change', () => {
          // This helps prevent the findDOMNode warning
        });
        
        // Set up proper event handling
        quill.on('selection-change', () => {
          // Handle selection changes
        });
      }
    }
  }, []);

  return (
    <div className={`${className} ${isFullHeight || isFixedHeight ? 'flex flex-col' : ''}`}>
      <ReactQuill
        ref={ref || quillRef}
        key={`quill-${readOnly ? 'readonly' : 'editable'}`}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
        style={(isFullHeight || isFixedHeight) ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}
      />
    </div>
  );
  });
  
  RichTextEditor.displayName = 'RichTextEditor';
  
  export default RichTextEditor;
